import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Progress } from "../components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { RotateCw, Repeat, Scale, Check, FlaskConical, Users2, Infinity as InfinityIcon } from "lucide-react";
import { toast } from "sonner";

const STRATEGIES = [
  { key: "round_robin", title: "Round-Robin", icon: Repeat,
    desc: "Fair rotation — the counselor assigned least recently gets the next lead." },
  { key: "affinity", title: "Course / Campus Affinity", icon: Scale,
    desc: "Score counselors by matching course (+2) and campus (+1); highest wins." },
  { key: "capacity", title: "Capacity Load-Balancing", icon: Users2,
    desc: "Send each lead to the counselor with the most remaining headroom." },
];

const FALLBACKS = [
  { v: "round_robin", label: "Round-Robin" },
  { v: "capacity", label: "Capacity" },
  { v: "none", label: "Leave unassigned" },
];

export default function AssignmentRules() {
  const [cfg, setCfg] = useState(null);
  const [counselors, setCounselors] = useState([]);
  const [options, setOptions] = useState({ courses: [], campuses: [] });
  const [editing, setEditing] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const [dry, setDry] = useState({ course: "", campus: "", result: null, busy: false });

  const loadCfg = useCallback(() => {
    api.get("/admin/assignment/config").then((r) => setCfg(r.data)).catch(() => {});
  }, []);
  const loadCounselors = useCallback(() => {
    api.get("/admin/assignment/counselors").then((r) => setCounselors(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadCfg(); loadCounselors(); }, [loadCfg, loadCounselors]);
  useEffect(() => {
    api.get("/analytics/filter-options")
      .then((r) => setOptions({ courses: r.data.courses || [], campuses: r.data.campuses || [] }))
      .catch(() => {});
  }, []);

  const saveCfg = async (patch) => {
    setCfg((c) => ({ ...c, ...patch }));
    try {
      const { data } = await api.put("/admin/assignment/config", patch);
      setCfg(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      loadCfg();
    }
  };

  const runPreview = async () => {
    setDry((d) => ({ ...d, busy: true }));
    try {
      const { data } = await api.post("/admin/assignment/preview", { course: dry.course, campus: dry.campus });
      setDry((d) => ({ ...d, result: data, busy: false }));
    } catch (e) {
      setDry((d) => ({ ...d, busy: false }));
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const backfill = async () => {
    setBackfilling(true);
    try {
      const { data } = await api.post("/admin/assignment/backfill");
      toast.success(
        `Backfill complete — assigned ${data.assigned} lead${data.assigned === 1 ? "" : "s"}` +
        (data.skipped ? `, skipped ${data.skipped}` : "")
      );
      loadCounselors();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBackfilling(false);
    }
  };

  if (!cfg) {
    return <div className="py-32 flex justify-center" data-testid="assignment-loading">
      <div className="w-7 h-7 border-2 border-slate-300 border-t-slate-800 rounded-full animate-spin" /></div>;
  }

  const isAffinity = cfg.strategy === "affinity";

  return (
    <div data-testid="assignment-rules-page" className="space-y-6 max-w-5xl">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Assignment Rules</h1>
        <p className="text-sm text-slate-500 mt-1">How incoming leads are routed to counselors.</p>
      </div>

      {/* master switch */}
      <Card className="p-5 border-slate-200 flex items-center justify-between gap-4" data-testid="assignment-master-switch">
        <div>
          <div className="font-semibold text-slate-800">Auto-assignment</div>
          <div className="text-sm text-slate-500">
            {cfg.enabled ? "New leads are routed automatically." : "New leads arrive unassigned."}
          </div>
        </div>
        <Switch data-testid="assignment-enabled-toggle" checked={!!cfg.enabled}
          onCheckedChange={(v) => saveCfg({ enabled: v })} />
      </Card>

      {/* strategy picker */}
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Strategy</div>
        <div className="grid gap-3 sm:grid-cols-3">
          {STRATEGIES.map((s) => {
            const active = cfg.strategy === s.key;
            return (
              <button key={s.key} type="button" data-testid={`strategy-card-${s.key}`}
                onClick={() => saveCfg({ strategy: s.key })}
                className={`text-left rounded-xl border p-4 transition-colors ${
                  active ? "border-slate-800 bg-slate-50 ring-1 ring-slate-800" : "border-slate-200 bg-white hover:border-slate-300"
                }`}>
                <div className="flex items-center justify-between">
                  <s.icon className={`w-5 h-5 ${active ? "text-slate-800" : "text-slate-400"}`} />
                  {active && <Check className="w-4 h-4 text-slate-800" />}
                </div>
                <div className="font-semibold text-slate-800 mt-2">{s.title}</div>
                <div className="text-xs text-slate-500 mt-1 leading-relaxed">{s.desc}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* modifiers */}
      <Card className="p-5 border-slate-200 space-y-4" data-testid="assignment-modifiers">
        <div className="flex items-start gap-3">
          <Checkbox id="respect_capacity" data-testid="respect-capacity-checkbox" checked={!!cfg.respect_capacity}
            onCheckedChange={(v) => saveCfg({ respect_capacity: !!v })} className="mt-0.5" />
          <div>
            <Label htmlFor="respect_capacity" className="font-medium">Respect capacity limits</Label>
            <div className="text-xs text-slate-500">Skip counselors who are at or over their max capacity.</div>
          </div>
        </div>

        <div className={`flex items-center justify-between gap-4 ${isAffinity ? "" : "opacity-50"}`}>
          <div>
            <Label className="font-medium">Affinity fallback</Label>
            <div className="text-xs text-slate-500">Used when no counselor has a course/campus match.</div>
          </div>
          <Select value={cfg.affinity_fallback} disabled={!isAffinity}
            onValueChange={(v) => saveCfg({ affinity_fallback: v })}>
            <SelectTrigger data-testid="affinity-fallback-select" className="w-[190px] h-9 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              {FALLBACKS.map((f) => <SelectItem key={f.v} value={f.v}>{f.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-wrap gap-6 pt-1">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox data-testid="assign-on-webhook-checkbox" checked={!!cfg.assign_on_webhook}
              onCheckedChange={(v) => saveCfg({ assign_on_webhook: !!v })} />
            Assign on webhook leads
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox data-testid="assign-on-walkin-checkbox" checked={!!cfg.assign_on_walkin}
              onCheckedChange={(v) => saveCfg({ assign_on_walkin: !!v })} />
            Assign on walk-in leads
          </label>
        </div>
      </Card>

      {/* dry run */}
      <Card className="p-5 border-slate-200" data-testid="assignment-dry-run">
        <div className="flex items-center gap-2 mb-3">
          <FlaskConical className="w-4 h-4 text-slate-500" />
          <h2 className="font-semibold text-slate-800">Dry run</h2>
          <span className="text-xs text-slate-400">— who would this lead go to?</span>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs">Course</Label>
            <Select value={dry.course || "__any__"} onValueChange={(v) => setDry((d) => ({ ...d, course: v === "__any__" ? "" : v }))}>
              <SelectTrigger data-testid="dry-run-course" className="w-[170px] h-9 text-sm mt-1"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__any__">Any</SelectItem>
                {options.courses.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Campus</Label>
            <Select value={dry.campus || "__any__"} onValueChange={(v) => setDry((d) => ({ ...d, campus: v === "__any__" ? "" : v }))}>
              <SelectTrigger data-testid="dry-run-campus" className="w-[170px] h-9 text-sm mt-1"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__any__">Any</SelectItem>
                {options.campuses.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button data-testid="dry-run-button" onClick={runPreview} disabled={dry.busy} className="bg-brand">
            {dry.busy ? "Checking…" : "Preview match"}
          </Button>
        </div>
        {dry.result && (
          <div className="mt-4 rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm" data-testid="dry-run-result">
            {dry.result.would_assign ? (
              <div>
                <span className="font-semibold text-slate-800">{dry.result.would_assign.name}</span>
                <span className="text-slate-500"> — {dry.result.would_assign.reason}</span>
                <span className="text-slate-400 font-mono text-xs ml-2">
                  load {dry.result.would_assign.load}/{dry.result.would_assign.capacity || "∞"}
                </span>
                {!dry.result.enabled && <Badge className="ml-2 bg-amber-100 text-amber-800">auto-assign is OFF</Badge>}
              </div>
            ) : (
              <span className="text-slate-500">No eligible counselor (check active flags / capacity).</span>
            )}
          </div>
        )}
      </Card>

      {/* backfill */}
      <Card className="p-5 border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="assignment-backfill">
        <div>
          <div className="font-semibold text-slate-800">Backfill unassigned leads</div>
          <div className="text-sm text-slate-500">Run the current strategy over every lead that has no counselor yet.</div>
        </div>
        <Button variant="outline" data-testid="backfill-button" onClick={backfill} disabled={backfilling}>
          <RotateCw className={`w-4 h-4 mr-1.5 ${backfilling ? "animate-spin" : ""}`} />
          {backfilling ? "Assigning…" : "Backfill now"}
        </Button>
      </Card>

      {/* counselor manager */}
      <Card className="border-slate-200" data-testid="counselor-manager">
        <div className="p-4 border-b border-slate-200">
          <h2 className="font-semibold text-slate-800">Counselors</h2>
          <p className="text-xs text-slate-500 mt-0.5">Capacity, course/campus affinity and routing weight.</p>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                {["Counselor", "Active", "Load", "Courses", "Campuses", ""].map((h) => (
                  <TableHead key={h} className="text-xs font-semibold uppercase tracking-wider text-slate-500">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {counselors.map((c, i) => {
                const pct = c.max_capacity ? Math.min(100, Math.round((c.current_load / c.max_capacity) * 100)) : 0;
                return (
                  <TableRow key={c.id} data-testid={`counselor-row-${i}`}>
                    <TableCell>
                      <div className="font-medium text-slate-800">{c.name}</div>
                      <div className="text-xs text-slate-400">{c.email}</div>
                    </TableCell>
                    <TableCell>
                      <Switch data-testid={`counselor-active-${i}`} checked={c.active}
                        onCheckedChange={async (v) => {
                          try {
                            const { data } = await api.put(`/admin/assignment/counselors/${c.id}`, { active: v });
                            setCounselors(data.profiles);
                          } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                        }} />
                    </TableCell>
                    <TableCell className="min-w-[150px]">
                      {c.max_capacity ? (
                        <div>
                          <div className="flex justify-between text-xs text-slate-500 mb-1">
                            <span className="font-mono">{c.current_load}/{c.max_capacity}</span><span>{pct}%</span>
                          </div>
                          <Progress value={pct} className="h-2" />
                        </div>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                          <InfinityIcon className="w-3.5 h-3.5" /> {c.current_load} · unlimited
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(c.courses || []).length
                          ? c.courses.map((x) => <Badge key={x} className="bg-blue-100 text-blue-800">{x}</Badge>)
                          : <span className="text-xs text-slate-400">Any</span>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(c.campuses || []).length
                          ? c.campuses.map((x) => <Badge key={x} className="bg-emerald-100 text-emerald-800">{x}</Badge>)
                          : <span className="text-xs text-slate-400">Any</span>}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="outline" size="sm" data-testid={`counselor-edit-${i}`} onClick={() => setEditing(c)}>Edit</Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Card>

      {editing && (
        <CounselorEditDialog
          counselor={editing}
          options={options}
          onClose={() => setEditing(null)}
          onSaved={(profiles) => { setCounselors(profiles); setEditing(null); }}
        />
      )}
    </div>
  );
}

function Chips({ all, selected, onToggle, activeClass }) {
  return (
    <div className="flex flex-wrap gap-2">
      {all.map((x) => {
        const on = selected.includes(x);
        return (
          <button key={x} type="button" onClick={() => onToggle(x)}
            className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
              on ? activeClass : "bg-white border-slate-200 text-slate-500 hover:border-slate-300"
            }`}>
            {x}
          </button>
        );
      })}
    </div>
  );
}

function CounselorEditDialog({ counselor, options, onClose, onSaved }) {
  const [unlimited, setUnlimited] = useState(!counselor.max_capacity);
  const [cap, setCap] = useState(counselor.max_capacity || 50);
  const [courses, setCourses] = useState(counselor.courses || []);
  const [campuses, setCampuses] = useState(counselor.campuses || []);
  const [weight, setWeight] = useState(counselor.weight || 1);
  const [busy, setBusy] = useState(false);

  const toggle = (setter, list) => (x) =>
    setter(list.includes(x) ? list.filter((y) => y !== x) : [...list, x]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/assignment/counselors/${counselor.id}`, {
        max_capacity: unlimited ? 0 : Math.max(0, Number(cap) || 0),
        courses, campuses, weight: Math.max(1, Number(weight) || 1),
      });
      toast.success("Counselor updated");
      onSaved(data.profiles);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="counselor-edit-dialog">
        <DialogHeader><DialogTitle>{counselor.name}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div>
            <div className="flex items-center justify-between">
              <Label>Max capacity</Label>
              <label className="flex items-center gap-2 text-xs text-slate-500">
                <Checkbox checked={unlimited} onCheckedChange={(v) => setUnlimited(!!v)} data-testid="edit-unlimited" /> Unlimited
              </label>
            </div>
            <Input type="number" min={1} value={cap} disabled={unlimited} data-testid="edit-capacity"
              onChange={(e) => setCap(e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label>Courses <span className="text-xs text-slate-400">(empty = any)</span></Label>
            <div className="mt-1.5">
              <Chips all={options.courses} selected={courses} onToggle={toggle(setCourses, courses)}
                activeClass="bg-blue-100 border-blue-300 text-blue-800" />
            </div>
          </div>
          <div>
            <Label>Campuses <span className="text-xs text-slate-400">(empty = any)</span></Label>
            <div className="mt-1.5">
              <Chips all={options.campuses} selected={campuses} onToggle={toggle(setCampuses, campuses)}
                activeClass="bg-emerald-100 border-emerald-300 text-emerald-800" />
            </div>
          </div>
          <div>
            <Label htmlFor="cw">Routing weight</Label>
            <Input id="cw" type="number" min={1} value={weight} data-testid="edit-weight"
              onChange={(e) => setWeight(e.target.value)} className="mt-1.5 w-28" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy} data-testid="counselor-edit-save" className="bg-brand">
              {busy ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
